import { useState } from 'react';
import { array, ord, string } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { LocalizedText, lt, useDialog } from '@mews/framework';
import { Link, Container, Stack, Title } from '@optimus-web/core';
import {
    OnboardingAuditActivityType,
    OnboardingAuditLogResult,
    OnboardingAuditLogs,
    useRepositorySuspenseQuery,
} from '@mews-commander/data-access';
import { ActivityLog, LogItemProps } from '@mews/b2b-ui';
import { TextKey } from '@mews-commander/enums';
import { LocalizedDateTimeFromUtc } from '@mews-commander/ui';
import { AuditLogDetailsDialog } from './AuditLogDetailsDialog';

interface CreateActivityLogItemsMapParams {
    auditLogs: Array<OnboardingAuditLogResult>;
    onLinkClick: (id: string) => void;
}

const createActivityLogItemsMap = ({ auditLogs, onLinkClick }: CreateActivityLogItemsMapParams) =>
    pipe(
        auditLogs,
        array.map(({ createdUtc, id, isReadyToGoLive, note, title, activityType }) => {
            const sharedProps = {
                stepName: title,
                key: `audit-log-${id}`,
                date: <LocalizedDateTimeFromUtc utcString={createdUtc} />,
                description: note,
            };

            if (activityType === OnboardingAuditActivityType.ReviewRequested) {
                return {
                    ...sharedProps,
                    variant: 'success',
                } as LogItemProps;
            }

            return {
                ...sharedProps,
                variant: isReadyToGoLive ? 'basic' : 'danger',
                link: (
                    <Link onClick={() => onLinkClick(id)}>
                        <LocalizedText textKey={TextKey.ViewMore} />
                    </Link>
                ),
            } as LogItemProps;
        })
    );

const orderByCreatedUtc: ord.Ord<OnboardingAuditLogResult> = pipe(
    ord.reverse(string.Ord),
    ord.contramap((audit) => audit.createdUtc)
);

const auditLogSelect = ({ auditLogs }: OnboardingAuditLogs) => pipe(auditLogs, array.sort(orderByCreatedUtc));

export const ActivityLogTab = () => {
    const [dialogProps, openDialog] = useDialog();
    const [selectedAuditId, setSelectedAuditId] = useState<string>();

    const { data: auditLogs } = useRepositorySuspenseQuery(
        'enterprises',
        'getAllOnboardingAuditLogs',
        {},
        {
            refetchOnWindowFocus: 'always',
            select: auditLogSelect,
        }
    );

    const handleOpenDialog = (id: string) => {
        setSelectedAuditId(id);
        openDialog();
    };

    const activityLogItems = createActivityLogItemsMap({ auditLogs, onLinkClick: handleOpenDialog });

    return (
        <>
            <Container padding={['300', '0', '0', '0']}>
                <Stack vertical spacing="300">
                    <Title level={2}>{lt('GoLiveAuditsActivityLog')}</Title>
                    <Container width="50%">
                        <ActivityLog items={activityLogItems} />
                    </Container>
                </Stack>
            </Container>
            {selectedAuditId ? <AuditLogDetailsDialog auditLogId={selectedAuditId} {...dialogProps} /> : null}
        </>
    );
};
